import React, { useState, useEffect, useCallback } from 'react';
import { Routes, Route, useParams, useNavigate, useLocation } from 'react-router-dom';
import Map from './components/Map';
import Sidebar from './components/Sidebar';
import AuthModal from './components/AuthModal';
import LocationConfirmModal from './components/LocationConfirmModal';
import ResizableDivider from './components/ResizableDivider';
import OnboardingModal from './components/OnboardingModal';
import { authAPI, locationsAPI, eventsAPI, truckAPI } from './api';
import { isWithinAllowedArea } from './config/boundaries';
import { geocodeAddress } from './utils/geocoding';
import { sanitizeText } from './utils/sanitize';

function AppContent() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [user, setUser] = useState(null);
  const [showAuth, setShowAuth] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [locations, setLocations] = useState([]);
  const [events, setEvents] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [filteredTruck, setFilteredTruck] = useState(null);
  const [serviceFilters, setServiceFilters] = useState([]);
  const [isSettingLocation, setIsSettingLocation] = useState(false);
  const [isGettingLocation, setIsGettingLocation] = useState(false);
  const [showAddressInput, setShowAddressInput] = useState(false);
  const [addressInput, setAddressInput] = useState('');
  const [isGeocodingAddress, setIsGeocodingAddress] = useState(false);
  const [showLocationConfirm, setShowLocationConfirm] = useState(false);
  const [pendingLocationData, setPendingLocationData] = useState(null);
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const saved = localStorage.getItem('sidebarWidth');
    return saved ? parseInt(saved, 10) : 400;
  });
  const [mapHeight, setMapHeight] = useState(() => {
    const saved = localStorage.getItem('mapHeight');
    return saved ? parseInt(saved, 10) : Math.round((window.innerHeight - 70) * 0.5);
  });
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 900);

  // Listen for window resize to detect mobile/desktop
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 900);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle slug changes and load filtered truck data
  useEffect(() => {
    const loadFilteredTruck = async () => {
      if (slug) {
        try {
          const res = await truckAPI.getBySlug(slug);
          setFilteredTruck(res.data);
        } catch (err) {
          console.error('Failed to load truck by slug:', err);
          setFilteredTruck(null);
          // Optionally redirect to home if truck not found
          if (err.response?.status === 404) {
            navigate('/', { replace: true });
          }
        }
      } else {
        setFilteredTruck(null);
      }
    };
    
    loadFilteredTruck();
  }, [slug, navigate]);

  const handleSidebarResize = useCallback((width) => {
    setSidebarWidth(width);
    localStorage.setItem('sidebarWidth', width.toString());
  }, []);

  const handleMapHeightResize = useCallback((height) => {
    setMapHeight(height);
    localStorage.setItem('mapHeight', height.toString());
  }, []);

  // Check for existing token on load
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      authAPI.getMe()
        .then(res => setUser(res.data))
        .catch(() => localStorage.removeItem('token'));
    }
  }, []);

  // Load locations and events
  useEffect(() => {
    // First cleanup any expired auto-delete events, then load fresh data
    const initializeData = async () => {
      try {
        await eventsAPI.cleanupExpired();
      } catch (err) {
        console.error('Failed to cleanup expired events:', err);
      }
      loadLocations();
      loadEvents();
    };
    initializeData();
  }, []);

  // Reload data when slug or service filters change
  useEffect(() => {
    loadLocations();
    loadEvents();
  }, [slug, serviceFilters]);

  const loadLocations = async () => {
    try {
      const res = await locationsAPI.getAll(slug, serviceFilters);
      setLocations(res.data);
    } catch (err) {
      console.error('Failed to load locations:', err);
    }
  };

  const loadEvents = async () => {
    try {
      const res = await eventsAPI.getAll(50, slug);
      setEvents(res.data);
    } catch (err) {
      console.error('Failed to load events:', err);
    }
  };

  const handleLogin = (userData, token) => {
    localStorage.setItem('token', token);
    setUser(userData);
    setShowAuth(false);
    
    // Prompt new users to set up their services
    if (!userData.services) {
      setTimeout(() => setShowOnboarding(true), 500);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setUser(null);
  };

  const handleSetLocationConfirmed = async (lat, lng) => {
    if (!user) {
      setShowAuth(true);
      return;
    }

    try {
      const res = await locationsAPI.create({
        latitude: lat,
        longitude: lng,
      });
      setLocations([res.data, ...locations.filter(l => l.user_id !== user.id)]);
      setIsSettingLocation(false);
      setSelectedLocation({ latitude: lat, longitude: lng });
    } catch (err) {
      console.error('Failed to set location:', err);
      alert('Failed to set location. Please try again.');
    }
  };

  const handleSetLocation = (lat, lng, locationType = 'map') => {
    if (!user) {
      setShowAuth(true);
      return;
    }

    // Store the location data and show confirmation modal
    setPendingLocationData({ lat, lng, locationType });
    setShowLocationConfirm(true);
  };

  const handleLocationConfirm = async () => {
    if (pendingLocationData) {
      await handleSetLocationConfirmed(pendingLocationData.lat, pendingLocationData.lng);
      setPendingLocationData(null);
    }
    setShowLocationConfirm(false);
  };

  const handleLocationCancel = () => {
    setPendingLocationData(null);
    setShowLocationConfirm(false);
    setIsGettingLocation(false);
    setIsGeocodingAddress(false);
  };

  const handleAddEvent = async (eventData) => {
    if (!user) {
      setShowAuth(true);
      return;
    }

    try {
      const res = await eventsAPI.create(eventData);
      setEvents([res.data, ...events]);
    } catch (err) {
      console.error('Failed to create event:', err);
      const errorMsg = err.response?.data?.error || err.message || 'Unknown error';
      alert(`Failed to create event: ${errorMsg}`);
    }
  };

  const handleDeleteEvent = async (eventId) => {
    try {
      await eventsAPI.delete(eventId);
      setEvents(events.filter(e => e.id !== eventId));
    } catch (err) {
      console.error('Failed to delete event:', err);
    }
  };

  const handleUpdateEvent = async (eventId, eventData) => {
    if (!user) {
      setShowAuth(true);
      return;
    }

    try {
      const res = await eventsAPI.update(eventId, eventData);
      setEvents(events.map(e => e.id === eventId ? res.data : e));
    } catch (err) {
      console.error('Failed to update event:', err);
      const errorMsg = err.response?.data?.error || err.message || 'Unknown error';
      alert(`Failed to update event: ${errorMsg}`);
    }
  };

  const handleToggleStatus = async (isOpen) => {
    if (!user) {
      setShowAuth(true);
      return;
    }

    try {
      const res = await locationsAPI.toggleStatus(isOpen);
      // Update the location in the locations array
      setLocations(locations.map(l => 
        l.user_id === user.id ? res.data : l
      ));
    } catch (err) {
      console.error('Failed to toggle status:', err);
      const errorMsg = err.response?.data?.error || err.message || 'Unknown error';
      alert(`Failed to update status: ${errorMsg}`);
    }
  };

  const handleSaveServices = async (services) => {
    if (!user) {
      setShowAuth(true);
      return;
    }

    try {
      const res = await authAPI.updateServices(services);
      // Update user state with new services
      setUser(res.data);
      // Reload locations to reflect updated services
      loadLocations();
    } catch (err) {
      console.error('Failed to save services:', err);
      throw err; // Re-throw so the modal can show error
    }
  };

  const handleServiceFilterChange = (newFilters) => {
    setServiceFilters(newFilters);
  };

  // Navigation helper functions
  const viewAllTrucks = () => {
    navigate('/');
  };

  const viewTruckOnly = (truckSlug) => {
    if (truckSlug) {
      navigate(`/${truckSlug}`);
    }
  };

  const getShareableUrl = (truckSlug) => {
    const baseUrl = window.location.origin;
    return truckSlug ? `${baseUrl}/${truckSlug}` : baseUrl;
  };

  const handleRemoveLocation = async () => {
    if (!user) return;

    const myLocation = locations.find(l => l.user_id === user.id);
    if (myLocation) {
      try {
        await locationsAPI.delete(myLocation.id);
        setLocations(locations.filter(l => l.id !== myLocation.id));
      } catch (err) {
        console.error('Failed to remove location:', err);
      }
    }
  };

  const useMyLocation = () => {
    if (!user) {
      setShowAuth(true);
      return;
    }

    if (!navigator.geolocation) {
      alert('Geolocation is not supported by your browser');
      return;
    }

    setIsGettingLocation(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        
        // Check if location is within allowed area
        if (!isWithinAllowedArea(latitude, longitude)) {
          setIsGettingLocation(false);
          alert('📍 Your current location is outside the allowed area.\n\nFood trucks can only pin locations within the Asheville area (15km radius from downtown).');
          return;
        }
        
        // Show confirmation modal instead of setting location directly
        handleSetLocation(latitude, longitude, 'current');
      },
      (error) => {
        setIsGettingLocation(false);
        switch (error.code) {
          case error.PERMISSION_DENIED:
            alert('Location access denied. Please enable location services.');
            break;
          case error.POSITION_UNAVAILABLE:
            alert('Location information unavailable.');
            break;
          case error.TIMEOUT:
            alert('Location request timed out.');
            break;
          default:
            alert('An error occurred getting your location.');
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  };

  const handleAddressSubmit = async (e) => {
    e.preventDefault();
    if (!addressInput.trim() || !user) return;

    setIsGeocodingAddress(true);
    try {
      const coords = await geocodeAddress(addressInput);
      if (coords) {
        if (!isWithinAllowedArea(coords.lat, coords.lng)) {
          alert('📍 This address is outside the allowed area.\n\nFood trucks can only pin locations within the Asheville area (15km radius from downtown).');
          setIsGeocodingAddress(false);
          return;
        }
        // Show confirmation modal instead of setting location directly
        handleSetLocation(coords.lat, coords.lng, 'address');
        setAddressInput('');
        setShowAddressInput(false);
      } else {
        alert('Could not find this address. Please try a more specific address.');
      }
    } catch (err) {
      console.error('Geocoding failed:', err);
      alert('Failed to find address. Please try again.');
    }
    setIsGeocodingAddress(false);
  };

  return (
    <div className="app">
      <header className="header">
        <div>
          <h1>🚚 AVL Flavor</h1>
          {filteredTruck && (
            <div className="filtered-truck-info">
              <span>Showing: {sanitizeText(filteredTruck.truck_name || filteredTruck.username)}</span>
              <button onClick={viewAllTrucks} className="view-all-btn">
                View All Trucks
              </button>
            </div>
          )}
        </div>
        <div className="header-actions">
          {user ? (
            <div className="user-info">
              <span>Welcome, {sanitizeText(user.truck_name || user.username)}!</span>
              <button onClick={handleLogout}>Logout</button>
            </div>
          ) : (
            <button onClick={() => setShowAuth(true)}>Login / Sign Up</button>
          )}
        </div>
      </header>

      <main className="main-content" style={isMobile ? { gridTemplateRows: `${mapHeight}px auto 1fr` } : { gridTemplateColumns: `1fr auto ${sidebarWidth}px` }}>
        <div className="map-container">
          <Map
            locations={locations}
            selectedLocation={selectedLocation}
            isSettingLocation={isSettingLocation}
            onSetLocation={(lat, lng) => handleSetLocation(lat, lng, 'map')}
            onSelectLocation={setSelectedLocation}
            user={user}
          />
          {user && (
            <div className="map-controls">
              <button
                onClick={useMyLocation}
                disabled={isGettingLocation || isSettingLocation || isGeocodingAddress}
              >
                {isGettingLocation ? '📡 Getting location...' : '📡 Use My Location'}
              </button>
              <button
                onClick={() => setIsSettingLocation(!isSettingLocation)}
                disabled={isSettingLocation || isGettingLocation || isGeocodingAddress}
              >
                {isSettingLocation ? 'Click on map to pin location' : '📍 Pin on Map'}
              </button>
              <button
                onClick={() => setShowAddressInput(!showAddressInput)}
                disabled={isSettingLocation || isGettingLocation || isGeocodingAddress}
              >
                🏠 Use Address
              </button>
              {isSettingLocation && (
                <button onClick={() => setIsSettingLocation(false)}>
                  Cancel
                </button>
              )}
              {showAddressInput && (
                <form className="address-form" onSubmit={handleAddressSubmit}>
                  <input
                    type="text"
                    placeholder="Enter address (e.g., 123 Main St, Asheville NC)"
                    value={addressInput}
                    onChange={(e) => setAddressInput(e.target.value)}
                    disabled={isGeocodingAddress}
                  />
                  <div className="address-form-buttons">
                    <button type="submit" disabled={isGeocodingAddress || !addressInput.trim()}>
                      {isGeocodingAddress ? '📍 Finding...' : '📍 Pin It'}
                    </button>
                    <button type="button" onClick={() => { setShowAddressInput(false); setAddressInput(''); }}>
                      Cancel
                    </button>
                  </div>
                </form>
              )}
              {locations.some(l => l.user_id === user.id) && (
                <button onClick={handleRemoveLocation}>
                  🗑️ Remove My Pin
                </button>
              )}
            </div>
          )}
        </div>

        <ResizableDivider 
          onResize={handleSidebarResize} 
          onResizeVertical={handleMapHeightResize}
          minWidth={250} 
          maxWidth={600}
          minHeight={150}
          maxHeight={Math.round((window.innerHeight - 70) * 0.8)}
          isMobile={isMobile}
        />

        <Sidebar
          locations={locations}
          events={events}
          user={user}
          filteredTruck={filteredTruck}
          serviceFilters={serviceFilters}
          onAddEvent={handleAddEvent}
          onUpdateEvent={handleUpdateEvent}
          onDeleteEvent={handleDeleteEvent}
          onSelectLocation={setSelectedLocation}
          onViewTruckOnly={viewTruckOnly}
          onGetShareableUrl={getShareableUrl}
          onToggleStatus={handleToggleStatus}
          onServiceFilterChange={handleServiceFilterChange}
          onOpenOnboarding={() => setShowOnboarding(true)}
        />
      </main>

      <footer className="app-footer">
        <p>© {new Date().getFullYear()} AVL Flavor. All rights reserved.</p>
      </footer>

      {showAuth && (
        <AuthModal
          onLogin={handleLogin}
          onClose={() => setShowAuth(false)}
        />
      )}
      
      {showOnboarding && (
        <OnboardingModal
          currentServices={user?.services}
          onSave={handleSaveServices}
          onClose={() => setShowOnboarding(false)}
          isNewUser={!user?.services}
        />
      )}
      
      {showLocationConfirm && (
        <LocationConfirmModal
          locationType={pendingLocationData?.locationType}
          onConfirm={handleLocationConfirm}
          onCancel={handleLocationCancel}
        />
      )}
    </div>
  );
}

function App() {
  return (
    <Routes>
      <Route path="/" element={<AppContent />} />
      <Route path="/:slug" element={<AppContent />} />
    </Routes>
  );
}

export default App;
